<template lang="pug">
.fb-preview-field(:class="{ 'has-label': hasLabel }")
    template(v-if="element.type === 'heading'")
        h3.fb-preview-heading {{ element.label }}
    .fb-preview-paragraph(v-else-if="element.type === 'paragraph'") {{ element.label }}
    .fb-preview-submit-wrap(v-else-if="element.type === 'submit'" :class="'is-' + (element.align || 'right')")
        button.fb-preview-submit(type="button" disabled) {{ element.label }}
    .fb-preview-hidden(v-else-if="element.role === 'hidden'")
        i.el-icon-fa-eye-slash.fas
        span {{ element.label }} · {{ $t('Hidden field') }}
        span.fb-preview-hidden-value(v-if="element.defaultValue") = {{ element.defaultValue }}
    label.fb-preview-consent(v-else-if="element.type === 'consent'")
        input(type="checkbox" disabled)
        span
            | {{ element.label }}
            span.fb-preview-required(v-if="element.required")  *
    template(v-else)
        label.fb-preview-label
            | {{ element.label }}
            span.fb-preview-required(v-if="element.required")  *
        textarea.fb-preview-input(
            v-if="element.type === 'textarea'"
            :rows="element.rows || 4"
            :placeholder="element.placeholder"
            disabled
        )
        .fb-preview-options(
            v-else-if="element.type === 'checkbox' || element.type === 'radio'"
            :class="{ 'is-inline': element.inline }"
        )
            label.fb-preview-option(v-for="option in element.options" :key="option.key")
                input(:type="element.type" disabled)
                span {{ option.value }}
        .fb-preview-select(v-else-if="element.type === 'country' || element.type === 'select'")
            span.fb-preview-select-text {{ dropdownLabel }}
            i.el-icon-fa-chevron-down.fas
        input.fb-preview-input(
            v-else-if="element.type === 'date'"
            :type="dateInputType"
            disabled
        )
        .fb-preview-slider(v-else-if="element.type === 'slider'")
            el-slider(
                :value="element.min || 0"
                :min="element.min || 0"
                :max="element.max || 100"
                :step="element.step || 1"
                disabled
            )
            span.fb-preview-slider-unit(v-if="element.unit") {{ element.unit }}
        input.fb-preview-input(
            v-else
            :placeholder="element.placeholder"
            disabled
        )
</template>

<i18n>
{
    "en": { "Hidden field": "Hidden field" },
    "de": { "Hidden field": "Verstecktes Feld" }
}
</i18n>

<script>
import countries from 'i18n-iso-countries'
import en from 'i18n-iso-countries/langs/en.json'
import de from 'i18n-iso-countries/langs/de.json'

countries.registerLocale(en)
countries.registerLocale(de)

export default {
    name: 'FormBuilderPreviewField',
    props: {
        element: { type: Object, required: true },
    },
    computed: {
        dateInputType() {
            if (this.element.mode === 'datetime') return 'datetime-local'
            if (this.element.mode === 'time') return 'time'
            return 'date'
        },
        dropdownLabel() {
            if (this.element.type === 'country') {
                const locale = this.$i18n && this.$i18n.locale === 'en' ? 'en' : 'de'
                if (this.element.defaultCountry) {
                    return countries.getName(this.element.defaultCountry, locale) || this.element.defaultCountry
                }
                return this.element.placeholder || '—'
            }
            // select: show placeholder, otherwise the first option as a hint
            if (this.element.placeholder) return this.element.placeholder
            const first = this.element.options && this.element.options[0]
            return (first && first.value) || '—'
        },
        // fields that render a standalone label above the control (→ eligible for the
        // left-label grid); heading/paragraph/submit/consent/hidden have no such label
        hasLabel() {
            return !['heading', 'paragraph', 'submit', 'consent'].includes(this.element.type)
                && this.element.role !== 'hidden'
        },
    },
}
</script>

<style lang="sass" scoped>
.fb-preview-field
    margin-bottom: .75rem
.fb-preview-heading
    margin: 0 0 .25rem
.fb-preview-paragraph
    color: #606266
.fb-preview-label
    display: block
    font-size: .85rem
    margin-bottom: .25rem
    color: #606266
.fb-preview-required
    color: $--color-danger
.fb-preview-input
    width: 100%
    padding: .5rem
    border: 1px solid #dcdfe6
    border-radius: 4px
    background: #f5f7fa
.fb-preview-select
    display: flex
    align-items: center
    justify-content: space-between
    gap: .5rem
    width: 100%
    padding: .5rem
    border: 1px solid #dcdfe6
    border-radius: 4px
    background: #f5f7fa
    color: #909399
    font-size: .85rem
    i
        font-size: .75rem
.fb-preview-hidden
    display: flex
    align-items: center
    gap: .4rem
    font-size: .78rem
    color: #b4b9c2
    font-style: italic
.fb-preview-hidden-value
    color: #909399
    font-style: normal
.fb-preview-consent
    display: flex
    align-items: flex-start
    gap: .4rem
    font-size: .85rem
    color: #606266
    input
        margin-top: .15rem
.fb-preview-slider
    display: flex
    align-items: center
    gap: .75rem
    ::v-deep .el-slider
        flex: 1 1 auto
.fb-preview-slider-unit
    flex: 0 0 auto
    color: #909399
    font-size: .85rem
.fb-preview-options
    display: flex
    flex-direction: column
    gap: .3rem
    &.is-inline
        flex-direction: row
        flex-wrap: wrap
        gap: .3rem 1rem
.fb-preview-option
    display: flex
    align-items: center
    gap: .4rem
    font-size: .85rem
    color: #606266
.fb-preview-submit-wrap
    display: flex
    &.is-left
        justify-content: flex-start
    &.is-right
        justify-content: flex-end
    &.is-full .fb-preview-submit
        width: 100%
.fb-preview-submit
    padding: .5rem 1.25rem
    border: none
    border-radius: 4px
    background: $--color-primary
    color: #fff
</style>
